import { useSignUpStore } from "~/stores";

const UTM_STORAGE_KEY = "utm_params";

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  ad_set_id?: string;
  ad_set_name?: string;
  ad_id?: string;
  ad_name?: string;
  gclid?: string;
}

const UTM_KEYS: (keyof UtmParams)[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "fbclid",
  "fbc",
  "fbp",
  "ad_set_id",
  "ad_set_name",
  "ad_id",
  "ad_name",
  "gclid",
];

let utmParamsMemory: UtmParams | null = null;
let messageListenerAdded = false; // Prevent duplicate listeners

const ALLOWED_ORIGINS = [
  "https://sarahlynnnutrition.com",
  "https://app.sarahlynnnutrition.com",
  "https://www.sarahlynnnutrition.com",
  "https://staging.app.sarahlynnnutrition.com",
];

// Helper to get cookie
function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const value = "; " + document.cookie;
  const parts = value.split("; " + name + "=");
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(";").shift();
    return cookieValue || null;
  }
  return null;
}

// Helper to set cookie
function setCookie(name: string, value: string, days: number = 1): void {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires}; SameSite=Lax; Secure`;
}

function parseUtmParams(): UtmParams | undefined {
  if (typeof window === "undefined") return undefined;

  const params = new URLSearchParams(window.location.search);
  const utm: UtmParams = {};

  let hasParams = false;

  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
      hasParams = true;
    }
  });

  return hasParams ? utm : undefined;
}

function storeUtmParams(params: UtmParams): void {
  utmParamsMemory = params;
  useSignUpStore.getState().setUtmParams(params);

  // Store in cookie (Safari-friendly)
  try {
    setCookie(UTM_STORAGE_KEY, JSON.stringify(params), 1);
  } catch (e) {}

  // Also try sessionStorage as backup
  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
  } catch (e) {}
}

function setupMessageListener(): void {
  if (typeof window === "undefined" || messageListenerAdded) return;

  messageListenerAdded = true;

  window.addEventListener("message", (event: MessageEvent) => {
    const isAllowed = ALLOWED_ORIGINS.some(
      (origin) =>
        event.origin === origin ||
        event.origin.endsWith(".sarahlynnnutrition.com"),
    );

    if (!isAllowed) {
      return;
    }

    if (event.data?.type === "UTM_PARAMS" && event.data?.data) {
      const newParams = {
        ...utmParamsMemory,
        ...event.data.data,
      };

      storeUtmParams(newParams);
    }
  });
}

export function captureUtmParams(): UtmParams | null {
  if (typeof window === "undefined") return null;

  // Setup listener first (only once)
  setupMessageListener();

  // Priority 1: URL params (freshest data)
  const urlParams = parseUtmParams();
  if (urlParams && Object.keys(urlParams).length > 0) {
    storeUtmParams(urlParams);
    return utmParamsMemory;
  }

  // Priority 2: Memory
  if (utmParamsMemory && Object.keys(utmParamsMemory).length > 0) {
    return utmParamsMemory;
  }

  // Priority 3: Zustand store
  const storeParams = useSignUpStore.getState().patient.utmParams;
  if (storeParams && Object.keys(storeParams).length > 0) {
    utmParamsMemory = storeParams;
    return utmParamsMemory;
  }

  // Priority 4: Cookie (Safari-friendly)
  try {
    const cookieValue = getCookie(UTM_STORAGE_KEY);
    if (cookieValue) {
      const parsed = JSON.parse(decodeURIComponent(cookieValue)) as UtmParams;
      if (Object.keys(parsed).length > 0) {
        storeUtmParams(parsed);
        return utmParamsMemory;
      }
    }
  } catch (e) {}

  // Priority 5: sessionStorage (backup)
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as UtmParams;
      if (Object.keys(parsed).length > 0) {
        storeUtmParams(parsed);
        return utmParamsMemory;
      }
    }
  } catch (e) {}

  return null;
}

export function getUtmParams(): UtmParams | null {
  if (typeof window === "undefined") return null;

  // Check memory first
  if (utmParamsMemory && Object.keys(utmParamsMemory).length > 0) {
    return utmParamsMemory;
  }

  // Check Zustand store
  const storeParams = useSignUpStore.getState().patient.utmParams;
  if (storeParams && Object.keys(storeParams).length > 0) {
    return storeParams;
  }

  // Check cookie
  try {
    const cookieValue = getCookie(UTM_STORAGE_KEY);
    if (cookieValue) {
      const parsed = JSON.parse(decodeURIComponent(cookieValue)) as UtmParams;
      if (Object.keys(parsed).length > 0) {
        return parsed;
      }
    }
  } catch (e) {}

  // Fall back to sessionStorage
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as UtmParams;
      if (Object.keys(parsed).length > 0) {
        return parsed;
      }
    }
  } catch (e) {}

  return null;
}

export function clearUtmParams(): void {
  if (typeof window === "undefined") return;
  utmParamsMemory = null;

  // Clear cookie
  try {
    document.cookie = `${UTM_STORAGE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } catch (e) {}

  // Clear sessionStorage
  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY);
  } catch (e) {}
}

// Initialize listener immediately when module loads
if (typeof window !== "undefined") {
  setupMessageListener();
}
